import { useEffect, useRef, useState } from "react";

// Mobile detection — disable expensive entry animations on small screens
const isMobile = () => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(max-width: 768px)").matches;
// Respect user's reduced-motion preference
const reducedMotion = () => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const Reveal = ({ children, delay = 0, className = "", as: Tag = "div", direction = "up" }) => {
  const ref = useRef(null);
  const skipAnim = useRef(false);
  if (skipAnim.current === false && (isMobile() || reducedMotion())) {
    skipAnim.current = true;
  }
  const [visible, setVisible] = useState(skipAnim.current);

  useEffect(() => {
    if (skipAnim.current) return;
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: "0px 0px -40px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // On mobile / reduced-motion: render statically, no transform, no transition.
  if (skipAnim.current) {
    return <Tag ref={ref} className={className}>{children}</Tag>;
  }

  const transform = visible
    ? "translate3d(0, 0, 0)"
    : direction === "left"
      ? "translate3d(-20px, 0, 0)"
      : direction === "right"
        ? "translate3d(20px, 0, 0)"
        : "translate3d(0, 20px, 0)";

  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform,
        transition: `opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
      }}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
